import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faGrid2 } from "@fortawesome/pro-light-svg-icons";
import { faFolderMinus } from "@fortawesome/pro-regular-svg-icons";
import Icon from "components/icon/Icon";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { SkeletonBar } from "components/skeleton";
import BrowseTree, { IBrowseTreeNode } from "components/browseTree/BrowseTree";
import { ICategories, IDepartments } from "redux/reducers/models/product";
import { IBrowseFilter } from "components/folders/productBrowseFilters";
import "./productBrowseTree.scss";

type IBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

interface IProductBrowseTreeProps {
  departments: IBrowseDepartment[];
  departmentsLoading?: boolean;
  uncategorizedCount: number;
  browseFilter: IBrowseFilter;
  onBrowseFilterChange: (filter: IBrowseFilter) => void;
  onGoToProducts?: () => void;
}

const departmentSkeletonRowKeys = [
  "department-skeleton-0",
  "department-skeleton-1",
  "department-skeleton-2",
  "department-skeleton-3",
  "department-skeleton-4",
];

function isFilterActive(browseFilter: IBrowseFilter, type: IBrowseFilter["type"], id?: number) {
  if (browseFilter.type !== type) {
    return false;
  }

  if (type === "all" || type === "uncategorized") {
    return true;
  }

  return browseFilter.id === id;
}

function buildBrowseNodes(departments: IBrowseDepartment[]): IBrowseTreeNode[] {
  return departments.map(department => ({
    id: `department-${department.id}`,
    name: department.title,
    label: department.title,
    children: (department.cateogries ?? []).map(category => ({
      id: `category-${category.id}`,
      name: category.title,
      label: category.title,
      children: (category.subcategories ?? []).map(subcategory => ({
        id: `subcategory-${subcategory.id}`,
        name: subcategory.title,
        label: subcategory.title,
      })),
    })),
  }));
}

function browseFilterFromNodeId(nodeId: string): IBrowseFilter | null {
  const separatorIndex = nodeId.indexOf("-");
  if (separatorIndex === -1) {
    return null;
  }

  const type = nodeId.slice(0, separatorIndex);
  const id = Number(nodeId.slice(separatorIndex + 1));

  if (type === "department") {
    return { type: "department", id };
  }

  if (type === "category") {
    return { type: "category", id };
  }

  if (type === "subcategory") {
    return { type: "subcategory", id };
  }

  return null;
}

function isNodeSelected(browseFilter: IBrowseFilter, nodeId: string) {
  const filter = browseFilterFromNodeId(nodeId);
  if (!filter) {
    return false;
  }

  return isFilterActive(browseFilter, filter.type, filter.id);
}

export default function ProductBrowseTree(props: IProductBrowseTreeProps) {
  const {
    departments,
    departmentsLoading = false,
    uncategorizedCount,
    browseFilter,
    onBrowseFilterChange,
    onGoToProducts,
  } = props;
  const { t } = useTranslation();
  const showNoDepartmentsEmptyState = !departmentsLoading && departments.length === 0;
  const browseNodes = useMemo(() => buildBrowseNodes(departments), [departments]);

  function handleNodeSelect(nodeId: string) {
    const filter = browseFilterFromNodeId(nodeId);
    if (filter) {
      onBrowseFilterChange(filter);
    }
  }

  return (
    <nav
      className="product-browse-tree"
      aria-label={t("Browse products")}
      aria-busy={departmentsLoading || undefined}
    >
      <button
        type="button"
        className={`browse-tree__row${isFilterActive(browseFilter, "all") ? " browse-tree__row--active" : ""}`}
        aria-current={isFilterActive(browseFilter, "all") ? "true" : undefined}
        onClick={() => onBrowseFilterChange({ type: "all" })}
      >
        <div className="browse-tree__row-inner product-browse-tree__row-inner--with-icon">
          <span className="product-browse-tree__icon-slot">
            <Icon icon={faGrid2} size="medium" />
          </span>
          <span className="browse-tree__label">{t("All products")}</span>
        </div>
      </button>

      <button
        type="button"
        className={`browse-tree__row${isFilterActive(browseFilter, "uncategorized") ? " browse-tree__row--active" : ""}`}
        aria-current={isFilterActive(browseFilter, "uncategorized") ? "true" : undefined}
        onClick={() => onBrowseFilterChange({ type: "uncategorized" })}
      >
        <div className="browse-tree__row-inner product-browse-tree__row-inner--with-icon">
          <span className="product-browse-tree__icon-slot">
            <Icon icon={faFolderMinus} size="medium" />
          </span>
          <span className="browse-tree__label">
            {t("Uncategorized")}
            <span className="product-browse-tree__count">
              {" • "}
              {departmentsLoading ? (
                <SkeletonBar className="product-browse-tree__count-skeleton-bar" />
              ) : (
                uncategorizedCount
              )}
            </span>
          </span>
        </div>
      </button>

      {departmentsLoading ? (
        <div className="product-browse-tree__skeleton" aria-hidden="true">
          {departmentSkeletonRowKeys.map(key => (
            <div key={key} className="product-browse-tree__skeleton-row">
              <SkeletonBar className="product-browse-tree__skeleton-bar" />
            </div>
          ))}
        </div>
      ) : showNoDepartmentsEmptyState ? (
        <div className="product-browse-tree__empty">
          <MessageDisplay
            icon="circle-minus"
            title={t("No departments yet")}
            content={t(
              "Assign a department to an existing product or create a new one to start browsing by department.",
            )}
            actions={
              onGoToProducts
                ? [{ label: t("Go to products"), type: "primary", action: onGoToProducts }]
                : undefined
            }
          />
        </div>
      ) : (
        <BrowseTree
          nodes={browseNodes}
          isSelected={nodeId => isNodeSelected(browseFilter, nodeId)}
          onSelect={handleNodeSelect}
        />
      )}
    </nav>
  );
}
